import { BrowserRouter, Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Header, Footer } from './components/Layout';
import ServicesPage from './pages/Services';
import About from './pages/About';
import Contact from './pages/Contact';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ProtectedRoute from './admin/ProtectedRoute';
import AdminLayout from './admin/AdminLayout';
import UsersList from './admin/UsersList';
import CreateUser from './admin/CreateUser';

/* ─── Professional Vector SVG Icon Helper ─── */
function SVGIcon({ name, className = "w-6 h-6 text-cta" }) {
  const icons = {
    shield: <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />,
    leaf: <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />,
    zap: <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />,
    check: <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />,
    search: <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />,
    chart: <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />,
    users: <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />,
    mapPin: <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />,
    layers: <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />,
    award: <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />,
    pest: <path strokeLinecap="round" strokeLinejoin="round" d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07" />,
    document: <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />,
  };
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      {icons[name] || icons.shield}
    </svg>
  );
}

/* ════════════════════════════════════════════════════════
   HOME PAGE — 10 High-End 3D & Curved Sections
   ════════════════════════════════════════════════════════ */
function Home() {
  const [openFaq, setOpenFaq] = useState(null);
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [pestCategory, setPestCategory] = useState('All 12 Pests');

  const specializedSolutionsData = [
    {
      title: 'Cockroach & Hygiene Control',
      desc: 'Targeted odorless gel & spray treatment for kitchens, dining halls & food storage.',
      category: 'Residential',
      iconName: 'shield',
      features: ['Odorless Gel', '1-Yr Warranty'],
      img: '/assets/service_cockroach.png',
      badge: 'FEATURED TREATMENT',
    },
    {
      title: 'Pre & Post Termite Shield',
      desc: 'Ultrasonic detection & deep soil chemical barrier protecting foundations.',
      category: 'Termite & Structure',
      iconName: 'leaf',
      features: ['10-Yr Guarantee', 'Soil Barrier'],
      img: '/assets/service_termite.png',
      badge: 'TERMITE SPECIALIST',
    },
    {
      title: 'Rat & Rodent Exclusion',
      desc: 'Multi-stage tamper-proof baiting stations & burrow sealing for zero re-entry.',
      category: 'Commercial',
      iconName: 'zap',
      features: ['Tamper-Proof Baits', 'Zero Re-entry'],
      img: '/assets/service_rodent.png',
      badge: 'SAFE EXCLUSION',
    },
    {
      title: 'General Pest Management',
      desc: 'Comprehensive shield against household insects, crawling pests & mosquitoes.',
      category: 'Residential',
      iconName: 'zap',
      features: ['100% Odorless', 'ISO Standard'],
      img: '/assets/service_general_pest.png',
      badge: 'COMPLETE SHIELD',
    },
    {
      title: 'Fly & Insect Control System',
      desc: 'UV insect light traps & anti-larval spray systems for hygienic food units.',
      category: 'Commercial',
      iconName: 'chart',
      features: ['UV Light Traps', 'FoSTaC Standard'],
      img: '/assets/service_fly.png',
      badge: 'HYGIENE SHIELD',
    },
    {
      title: 'Bird Netting & Spikes',
      desc: 'Durable HD-polyethylene bird netting & stainless steel anti-perch spikes.',
      category: 'Termite & Structure',
      iconName: 'document',
      features: ['UV Stabilized', 'Zero Harm'],
      img: '/assets/service_bird.png',
      badge: 'HUMANE BARRIER',
    },
  ];

  // Auto-rotate 3D Hero Cards every 3 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveCardIndex((prev) => (prev + 1) % 3);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const heroCardsData = [
    {
      id: 0,
      title: 'Cockroach & Hygiene Control',
      pill: 'AI Precision Gel',
      desc: 'Odorless gel & spray treatment for kitchen, dining and storage areas.',
      bg: '/assets/images/hero_cockroach.png',
      iconName: 'shield',
      metric: '100% Odorless',
      badge: 'FEATURED SERVICE',
    },
    {
      id: 1,
      title: 'Radar Anti-Termite Shield',
      pill: 'Pre & Post Construction',
      desc: 'Ultrasonic detection & deep soil barrier anti-termite shield treatment.',
      bg: '/assets/images/hero_termite.png',
      iconName: 'leaf',
      metric: '10-Yr Warranty',
      badge: 'TERMITE SPECIALIST',
    },
    {
      id: 2,
      title: 'Thermal Bedbug Eradication',
      pill: '100% Steam Removal',
      desc: 'Eco-friendly thermal heat steamer & chemical-free mattress sanitization.',
      bg: '/assets/images/hero_bedbug.png',
      iconName: 'zap',
      metric: 'Zero Chemicals',
      badge: 'BED BUG EXPERT',
    },
  ];

  const frontCard = heroCardsData[activeCardIndex];
  const leftCard = heroCardsData[(activeCardIndex + 2) % 3];
  const rightCard = heroCardsData[(activeCardIndex + 1) % 3];

  return (
    <main className="overflow-hidden bg-bg">

      {/* ─── 1. HERO (AI Tech 3D Grid Showcase) ─── */}
      <section className="relative min-h-[92vh] flex items-center px-4 sm:px-6 md:px-16 py-12 sm:py-16 overflow-hidden bg-gradient-to-b from-surface-alt via-bg to-bg">
        <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center relative z-10 py-4 sm:py-6">

          {/* Left Column: Hero Copy & Actions */}
          <div className="lg:col-span-7 animate-rise text-left">
            <div className="inline-flex items-center gap-2.5 px-3.5 sm:px-5 py-2 sm:py-2.5 rounded-full bg-accent-soft/90 text-accent-muted text-[10px] sm:text-xs font-bold tracking-widest uppercase mb-5 sm:mb-6 border border-accent/30 shadow-2xs backdrop-blur-md max-w-full">
              <span className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-cta animate-ping shrink-0" />
              <span className="truncate">ISO 9001:2015 CERTIFIED COMPANY</span>
            </div>

            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-primary leading-[1.12] mb-5 sm:mb-6 tracking-tight">
              Government Approved<br />
              <span className="text-cta">Precision Pest Elimination.</span>
            </h1>

            <p className="text-xs sm:text-base md:text-lg text-txt-secondary max-w-xl leading-relaxed mb-6 sm:mb-7 font-medium">
              ISO-certified &amp; FoSTaC trained pest control engineered for food, pharma and dairy industry across Maharashtra. Odorless, eco-safe, guaranteed.
            </p>

            {/* Audit Qualification Highlight Box */}
            <div className="mb-8 p-3.5 sm:p-4 rounded-2xl bg-white/90 border border-accent/40 shadow-sm backdrop-blur-md max-w-xl flex items-start gap-3 border-l-4 border-l-cta">
              <div className="w-8 h-8 rounded-xl bg-accent-soft text-cta grid place-items-center shrink-0 mt-0.5 shadow-2xs">
                <SVGIcon name="shield" className="w-4 h-4 text-cta" />
              </div>
              <p className="text-[11px] sm:text-xs text-primary font-medium leading-relaxed m-0">
                Our team is fully qualified for <strong className="text-cta font-black">AIB, ISO, USFDA, FDA, HACCP, HALAL</strong> and <strong className="text-cta font-black">FSSAI</strong> audits.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5 sm:gap-4 mb-8 sm:mb-10 w-full">
              <Link to="/contact" className="btn-flash inline-flex items-center justify-center gap-2 sm:gap-3 bg-cta hover:bg-cta-hover text-white font-extrabold text-sm sm:text-base px-6 sm:px-8 py-4 sm:py-4.5 rounded-2xl glow-cta no-underline text-center w-full sm:w-auto">
                Book Free Inspection <span className="text-lg sm:text-xl">→</span>
              </Link>
              <a href="tel:+918788895445" className="inline-flex items-center justify-center gap-2.5 bg-white text-primary font-bold text-sm sm:text-base px-6 sm:px-8 py-4 sm:py-4.5 rounded-2xl hover:bg-slate-50 transition-all no-underline shadow-md border border-slate-200 text-center w-full sm:w-auto">
                <span>📞</span> +91 87888 95445
              </a>
            </div>

            {/* Quick Metrics Bar */}
            <div className="grid grid-cols-3 gap-2.5 sm:gap-4 pt-6 border-t border-border max-w-lg">
              {[
                ['26+ Yrs', 'Proven Experience'],
                ['500+', 'Corporate Clients'],
                ['11 Services', 'Custom Solutions'],
              ].map(([val, label]) => (
                <div key={val} className="p-2.5 sm:p-3.5 rounded-2xl bg-white/80 border border-slate-200 backdrop-blur-sm shadow-2xs text-center sm:text-left">
                  <span className="font-heading text-sm sm:text-lg md:text-xl font-bold text-primary block leading-snug">{val}</span>
                  <span className="text-[9px] sm:text-[10px] uppercase font-bold tracking-wider text-txt-muted block leading-tight">{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column: 3-Second Auto-Rotating 3D Stack Showcase */}
          <div className="lg:col-span-5 relative flex flex-col items-center justify-center py-2 sm:py-6 min-h-[380px] sm:min-h-[460px] w-full">

            <div className="relative w-full max-w-sm flex items-center justify-center min-h-[340px] sm:min-h-[410px]">
              {/* Stacked Left Card (Previous Card) */}
              <div
                onClick={() => setActiveCardIndex(leftCard.id)}
                className="hidden sm:flex absolute -left-6 bottom-2 w-60 h-72 rounded-3xl overflow-hidden shadow-lg border-2 border-white/80 transform -rotate-6 hover:rotate-0 hover:scale-105 transition-all duration-700 z-0 opacity-80 cursor-pointer group"
              >
                <div
                  className="w-full h-full bg-cover bg-center relative"
                  style={{ backgroundImage: `url('${leftCard.bg}')` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-primary-dark/90 via-primary-dark/30 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <span className="text-[10px] font-extrabold uppercase bg-accent-bright/20 text-accent-bright px-2.5 py-1 rounded-full border border-accent/40 inline-block mb-1">
                      {leftCard.pill}
                    </span>
                    <b className="block text-xs font-bold leading-tight">{leftCard.title}</b>
                  </div>
                </div>
              </div>

              {/* Stacked Right Card (Next Card) */}
              <div
                onClick={() => setActiveCardIndex(rightCard.id)}
                className="hidden sm:flex absolute -right-6 -top-2 w-60 h-72 rounded-3xl overflow-hidden shadow-lg border-2 border-white/80 transform rotate-6 hover:rotate-0 hover:scale-105 transition-all duration-700 z-0 opacity-80 cursor-pointer group"
              >
                <div
                  className="w-full h-full bg-cover bg-center relative"
                  style={{ backgroundImage: `url('${rightCard.bg}')` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-primary-dark/90 via-primary-dark/30 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <span className="text-[10px] font-extrabold uppercase bg-accent-bright/20 text-accent-bright px-2.5 py-1 rounded-full border border-accent/40 inline-block mb-1">
                      {rightCard.pill}
                    </span>
                    <b className="block text-xs font-bold leading-tight">{rightCard.title}</b>
                  </div>
                </div>
              </div>

              {/* Main Center Active Front 3D Card (Rotates Every 3 Sec) */}
              <div key={frontCard.id} className="card-3d-hologram w-full overflow-hidden shadow-2xl z-20 relative group transition-all duration-700 animate-rise rounded-3xl">
                <div
                  className="h-48 sm:h-64 bg-cover bg-center transition-transform duration-700 group-hover:scale-105 relative"
                  style={{ backgroundImage: `url('${frontCard.bg}')` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-primary-dark/80 via-primary-dark/20 to-transparent" />

                  {/* Floating Status Pill */}
                  <span className="absolute top-4 left-4 glass backdrop-blur-md text-primary font-bold text-xs px-3.5 py-1.5 rounded-full border border-white/80 shadow-md flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-success shrink-0" />
                    {frontCard.pill}
                  </span>
                </div>

                <div className="p-5 sm:p-6 bg-surface relative">
                  <span className="absolute -top-6 right-6 w-12 h-12 rounded-2xl bg-accent-soft border border-accent/30 text-cta flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <SVGIcon name={frontCard.iconName} className="w-6 h-6 text-cta" />
                  </span>

                  <b className="text-[10px] sm:text-xs font-extrabold text-cta uppercase tracking-widest block mb-1">{frontCard.badge}</b>
                  <h3 className="font-heading text-lg sm:text-xl font-bold text-primary mb-2">{frontCard.title}</h3>
                  <p className="text-xs text-txt-muted leading-relaxed mb-4">
                    {frontCard.desc}
                  </p>

                  <div className="flex items-center justify-between pt-3 border-t border-border">
                    <span className="text-xs font-bold text-primary flex items-center gap-1.5">
                      <span className="text-accent-muted font-bold">✓</span> {frontCard.metric}
                    </span>
                    <Link to="/services" className="text-xs font-bold text-cta hover:translate-x-1 transition-transform no-underline flex items-center gap-1">
                      Book Treatment →
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* 3-Second Rotation Indicator Dots */}
            <div className="flex items-center gap-2 mt-6 sm:mt-8 z-30">
              {heroCardsData.map((c, idx) => (
                <button
                  key={c.id}
                  onClick={() => setActiveCardIndex(idx)}
                  className={`h-2.5 rounded-full transition-all duration-500 cursor-pointer ${idx === activeCardIndex
                    ? 'w-8 bg-accent-bright shadow-sm'
                    : 'w-2.5 bg-slate-300 hover:bg-accent-muted'
                    }`}
                  aria-label={`Show card ${idx + 1}`}
                />
              ))}
            </div>

          </div>

        </div>
      </section>

      {/* Curved Divider Top: Hero -> Marquee */}
      <div className="w-full overflow-hidden leading-none bg-bg -mt-1 -mb-1 relative z-10 pointer-events-none">
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-14 text-accent-bright fill-current">
          <path d="M0,0 C150,90 350,-40 500,50 C650,140 900,10 1200,60 L1200,120 L0,120 Z" />
        </svg>
      </div>

      {/* ─── 2. TRUST MARQUEE BAR (Ultra-Modern 3D Ribbon) ─── */}
      <section className="py-5 bg-gradient-to-r from-primary-dark via-primary to-primary-deep border-y-2 border-accent-bright/60 shadow-xl overflow-hidden relative z-20">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex items-center gap-10 px-4">
              {[
                ['shield', 'ISO 9001:2015 Certified'],
                ['document', 'FoSTaC Qualified Team'],
                ['award', 'FSSAI Food Audit Ready'],
                ['zap', 'HACCP Compliant'],
                ['leaf', 'Govt Approved Eco Chemicals'],
                ['check', '24/7 Emergency Dispatch'],
                ['users', '500+ Satisfied Corporate Clients'],
                ['mapPin', '8 Maharashtra Branches'],
              ].map(([iconName, badge]) => (
                <span key={badge + i} className="flex items-center gap-2.5 text-xs md:text-sm font-extrabold text-white whitespace-nowrap bg-white/10 backdrop-blur-md px-4 py-2 rounded-2xl border border-white/20 shadow-md hover:bg-white/20 transition-colors cursor-default">
                  <SVGIcon name={iconName} className="w-4 h-4 text-accent-bright" />
                  <span>{badge}</span>
                  <span className="text-accent-bright opacity-60 ml-3">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* ─── 3. WHY CHOOSE US (Clean Professional Cards) ─── */}
      <section className="py-14 sm:py-20 px-4 sm:px-6 md:px-16 bg-bg relative">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <span className="inline-flex items-center gap-2 px-3.5 sm:px-4 py-1.5 rounded-full bg-accent-soft text-accent-muted text-[10px] sm:text-xs font-bold tracking-widest uppercase mb-3 sm:mb-4 border border-accent/20 shadow-2xs">
              WHY PREVENTIVE PEST TERMINATORS
            </span>
            <h2 className="font-heading text-2xl sm:text-4xl md:text-5xl font-bold text-primary tracking-tight mb-3 sm:mb-4">
              Protection engineered for <span className="text-cta">peace of mind.</span>
            </h2>
            <p className="text-txt-muted text-xs sm:text-base md:text-lg max-w-2xl mx-auto leading-relaxed font-medium">
              We combine certified technical expertise with family-safe products to ensure permanent pest eradication.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {[
              ['shield', 'Certified Professionals', 'ISO 9001:2015 & FoSTaC certified team with specialized industrial training.'],
              ['leaf', 'Eco & Pet Safe', '100% odorless, government-approved non-toxic formulations.'],
              ['zap', 'Rapid 24/7 Service', 'Swift emergency dispatch across 8 major districts in Maharashtra.'],
              ['check', 'Guaranteed Outcome', 'Comes with scheduled re-inspections and post-treatment assurance.'],
            ].map(([iconName, title, desc]) => (
              <article
                key={title}
                className="group p-6 sm:p-7 flex flex-col justify-between rounded-[28px] bg-white text-primary border-2 border-slate-200/90 hover:border-cta shadow-md hover:shadow-[0_20px_50px_rgba(23,75,53,0.15),0_0_25px_rgba(169,217,87,0.4)] transition-all duration-500 hover:-translate-y-2 hover:scale-[1.02] cursor-default relative overflow-hidden"
              >
                {/* Concentric Pulsing Wave Rings */}
                <div className="absolute top-8 left-8 w-44 h-44 -mt-10 -ml-10 rounded-full border border-cta/30 opacity-0 group-hover:opacity-100 scale-50 group-hover:animate-ping pointer-events-none z-0" />
                <div className="absolute top-8 left-8 w-64 h-64 -mt-20 -ml-20 rounded-full bg-gradient-to-br from-accent/25 via-cta/15 to-transparent opacity-0 group-hover:opacity-100 scale-0 group-hover:scale-125 transition-all duration-1000 ease-out pointer-events-none z-0" />

                <div className="relative z-10">
                  <div className="w-12 h-12 rounded-2xl bg-accent-soft border border-accent/40 text-cta group-hover:bg-cta group-hover:text-white flex items-center justify-center mb-5 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-sm relative">
                    <SVGIcon name={iconName} className="w-6 h-6 fill-current" />
                    {/* Icon Node Pulse Beacon */}
                    <span className="absolute inset-0 rounded-2xl bg-cta opacity-0 group-hover:animate-ping pointer-events-none" />
                  </div>
                  <h3 className="font-heading text-base sm:text-lg font-bold text-primary group-hover:text-cta mb-2 transition-colors duration-300">{title}</h3>
                  <p className="text-xs text-txt-muted leading-relaxed font-medium">{desc}</p>
                </div>

                <div className="mt-5 pt-3 border-t border-slate-100 flex items-center justify-between text-[10px] font-bold text-accent-muted group-hover:text-cta transition-colors relative z-10">
                  <span>Certified Standard</span>
                  <span className="group-hover:translate-x-1 transition-transform">✦</span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Curved Divider Middle: Why Choose Us -> Services Showcase */}
      <div className="w-full overflow-hidden leading-none bg-surface-alt -mt-1 -mb-1 relative z-10 pointer-events-none">
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-14 text-accent-bright fill-current">
          <path d="M0,0 C150,90 350,-40 500,50 C650,140 900,10 1200,60 L1200,120 L0,120 Z" />
        </svg>
      </div>

      {/* ─── 4. SPECIALIZED SOLUTIONS (Deals of Arya 2-Column Split Layout) ─── */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 md:px-16 bg-gradient-to-b from-primary-dark via-primary to-primary-deep text-white relative overflow-hidden">
        {/* Ambient Glow Bulbs */}
        <div className="absolute right-0 top-1/4 w-96 h-96 rounded-full bg-accent-bright/10 blur-3xl pointer-events-none" />
        <div className="absolute left-0 bottom-1/4 w-80 h-80 rounded-full bg-cta/15 blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center relative z-10">

          {/* Left Column: Headline, Narrative & Primary CTA */}
          <div className="lg:col-span-5 space-y-6">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 text-accent-bright text-xs font-extrabold tracking-widest uppercase border border-white/20 shadow-sm backdrop-blur-md">
              <span className="w-2 h-2 rounded-full bg-accent-bright animate-ping" />
              SPECIALIZED SOLUTIONS
            </span>

            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight leading-tight">
              Our services have gained the trust of <span className="text-accent-bright">thousands of customers.</span>
            </h2>

            <p className="text-white/80 text-sm sm:text-base leading-relaxed font-normal">
              Targeted, ISO-standard treatment plans customized for residential, commercial &amp; industrial premises across Maharashtra. We ensure 100% permanent pest eradication.
            </p>

            <div className="pt-2 flex flex-wrap gap-4">
              <Link to="/contact" className="text-white bg-cta hover:bg-cta-hover px-7 py-3.5 rounded-2xl text-xs sm:text-sm font-extrabold shadow-lg no-underline inline-flex items-center gap-2 glow-cta transition-all hover:-translate-y-0.5">
                Book Free Inspection <span>→</span>
              </Link>
              <Link to="/services" className="px-6 py-3.5 rounded-2xl text-xs sm:text-sm font-bold text-white bg-white/10 hover:bg-white/20 border border-white/20 transition-all no-underline inline-flex items-center gap-2">
                Explore All Services <span>→</span>
              </Link>
            </div>
          </div>

          {/* Right Column: 6-Block Service Card Grid (Deals of Arya 3x2 Grid) */}
          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-5">
            {specializedSolutionsData.map((service) => (
              <Link
                to="/services"
                key={service.title}
                className="group p-5 rounded-3xl bg-emerald-950/50 hover:bg-emerald-900/60 border border-white/15 hover:border-accent-bright/50 shadow-xl hover:shadow-2xl transition-all duration-300 flex flex-col justify-between no-underline hover:-translate-y-1.5 relative overflow-hidden"
              >
                <div className="absolute inset-0 -left-full w-full bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12 group-hover:left-[200%] transition-all duration-1000 ease-in-out pointer-events-none" />

                <div>
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-accent-bright/30 to-cta/30 border border-white/20 text-accent-bright flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-6 transition-transform shadow-md">
                    <SVGIcon name={service.iconName} className="w-6 h-6 text-accent-bright" />
                  </div>
                  <span className="text-[9px] font-extrabold uppercase tracking-widest text-accent-bright block mb-1">
                    {service.badge}
                  </span>
                  <h3 className="font-heading text-sm sm:text-base font-extrabold text-white mb-1.5 group-hover:text-accent-bright transition-colors leading-snug">
                    {service.title}
                  </h3>
                  <p className="text-[11px] text-white/70 leading-relaxed font-normal line-clamp-2">
                    {service.desc}
                  </p>
                </div>
                <div className="mt-4 pt-2 flex items-center justify-between text-[10px] font-bold text-accent-bright">
                  <span>Book Service</span>
                  <span className="group-hover:translate-x-1 transition-transform">→</span>
                </div>
              </Link>
            ))}
          </div>

        </div>
      </section>

      {/* ─── 5. PEST TYPES (Unique 3D Tactile Token Keys) ─── */}
      <section className="py-10 sm:py-14 md:py-20 px-3 min-[480px]:px-5 sm:px-6 md:px-16 bg-bg relative">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <span className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1 sm:py-1.5 rounded-full bg-accent-soft text-accent-muted text-[9px] sm:text-xs font-bold tracking-widest uppercase mb-2.5 sm:mb-4 border border-accent/20 shadow-2xs">
              TARGETED PEST CONTROL
            </span>
            <h2 className="font-heading text-xl min-[480px]:text-2xl sm:text-3xl md:text-5xl font-bold text-primary tracking-tight mb-2.5 sm:mb-4 px-2 sm:px-0">
              We eliminate <span className="text-cta">every pest species.</span>
            </h2>
            <p className="text-txt-muted text-[11px] min-[480px]:text-xs sm:text-sm md:text-base max-w-2xl mx-auto leading-relaxed font-medium px-2 sm:px-0">
              Custom specialized treatments tailored for homes, commercial complexes, and food manufacturing units.
            </p>
          </div>

          {/* Master Unified Group Container Hub */}
          <div className="p-3 min-[480px]:p-4 sm:p-7 md:p-9 rounded-2xl min-[480px]:rounded-3xl md:rounded-[32px] bg-white border border-slate-200/90 shadow-xl relative overflow-hidden">
            {/* Background Ambient Spotlights */}
            <div className="absolute top-0 right-0 w-48 sm:w-80 h-48 sm:h-80 bg-accent-bright/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-40 sm:w-72 h-40 sm:h-72 bg-cta/10 rounded-full blur-3xl pointer-events-none" />

            {/* Category Filter Pills Bar */}
            <div className="grid grid-cols-2 min-[480px]:flex min-[480px]:flex-wrap items-center justify-center gap-1.5 min-[480px]:gap-2 mb-4 sm:mb-8 relative z-10">
              {['All 12 Pests', 'Insects & Bugs', 'Rodents & Reptiles', 'Structural & Birds'].map((cat, idx) => (
                <button
                  key={cat}
                  onClick={() => setPestCategory(cat)}
                  className={`px-2.5 min-[480px]:px-3.5 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl text-[10px] min-[480px]:text-[11px] sm:text-xs font-bold transition-all duration-300 cursor-pointer ${pestCategory === cat
                    ? 'bg-cta text-white shadow-md glow-cta scale-105'
                    : 'bg-surface-alt text-txt-secondary hover:bg-slate-100 hover:text-primary'
                    }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Grid of Pest Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 min-[480px]:gap-3 sm:gap-3.5 md:gap-4 relative z-10">
              {[
                ['🪳', 'Cockroach', 'Kitchen & drain treatment', 'Insects & Bugs'],
                ['🐀', 'Rodents', 'Baiting & burrow sealing', 'Rodents & Reptiles'],
                ['🐜', 'Ants', 'Colony eradication', 'Insects & Bugs'],
                ['🕷️', 'Spiders', 'Web removal & barrier', 'Insects & Bugs'],
                ['🪰', 'Flies', 'Machine & trap setup', 'Insects & Bugs'],
                ['🐍', 'Snakes', 'Safe rescue & relocation', 'Rodents & Reptiles'],
                ['🦎', 'Lizards', 'Repellent treatment', 'Rodents & Reptiles'],
                ['🐦', 'Birds', 'Spikes & netting setup', 'Structural & Birds'],
                ['🌿', 'Termites', 'Pre & post construction', 'Structural & Birds'],
                ['🌱', 'Weeds', 'Yard & lawn clearing', 'Structural & Birds'],
                ['🦟', 'Mosquitoes', 'Fogging & anti-larval', 'Insects & Bugs'],
                ['🐛', 'Bed Bugs', 'Thermal & chemical removal', 'Insects & Bugs'],
              ]
                .filter(([_, __, ___, cat]) => pestCategory === 'All 12 Pests' || cat === pestCategory)
                .map(([icon, name, desc]) => (
                  <div
                    key={name}
                    className="group p-2.5 min-[480px]:p-3.5 sm:p-4 rounded-xl sm:rounded-2xl bg-surface-alt/70 hover:bg-white border border-slate-200/80 hover:border-cta shadow-2xs hover:shadow-[0_12px_30px_rgba(23,75,53,0.15),0_0_20px_rgba(169,217,87,0.4)] transition-all duration-400 cursor-pointer relative overflow-hidden flex flex-col justify-between"
                  >
                    {/* Flashing Glass Sheen Beam */}
                    <div className="absolute inset-0 -left-full w-full bg-gradient-to-r from-transparent via-white/50 to-transparent skew-x-12 group-hover:left-[200%] transition-all duration-700 ease-in-out pointer-events-none z-10" />

                    {/* Corner Diagonal Pulse Wave */}
                    <div className="absolute inset-0 bg-gradient-to-br from-accent-soft/60 via-accent/15 to-cta/10 opacity-0 group-hover:opacity-100 -translate-x-full -translate-y-full group-hover:translate-x-0 group-hover:translate-y-0 transition-all duration-700 ease-out pointer-events-none z-0" />

                    <div className="flex items-center gap-2 min-[480px]:gap-2.5 sm:gap-3 mb-1.5 sm:mb-2.5 relative z-20">
                      <div className="w-9 h-9 min-[480px]:w-10 min-[480px]:h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-white border border-accent/30 text-cta flex items-center justify-center shrink-0 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-2xs">
                        <span className="text-lg min-[480px]:text-xl sm:text-3xl drop-shadow-xs">{icon}</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-heading text-[11px] min-[480px]:text-xs sm:text-sm font-extrabold text-primary group-hover:text-cta transition-colors leading-tight">
                          {name}
                        </h3>
                        <span className="text-[8px] min-[480px]:text-[9px] font-bold text-cta bg-accent-soft px-1.5 sm:px-2 py-0.5 rounded-full border border-accent/20 inline-block mt-0.5">
                          ✓ Guaranteed
                        </span>
                      </div>
                    </div>

                    <p className="text-[9px] min-[480px]:text-[10px] sm:text-[11px] text-txt-muted leading-relaxed font-medium line-clamp-2 relative z-20">
                      {desc}
                    </p>
                  </div>
                ))}
            </div>

            {/* Bottom Guarantee Banner Bar */}
            <div className="mt-4 sm:mt-6 pt-3 sm:pt-5 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-3 relative z-10 text-center sm:text-left">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-cta animate-ping" />
                <span className="text-[10px] sm:text-xs font-bold text-primary">100% Permanent Eradication Guarantee Included</span>
              </div>
              <Link to="/contact" className="text-[10px] sm:text-xs font-extrabold text-cta hover:text-cta-hover flex items-center gap-1 no-underline">
                Request Pest Inspection <span>→</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ─── 6. OUR WORKFLOW (3D Gem Execution Pipeline) ─── */}
      <section className="py-20 px-6 md:px-16 bg-surface relative overflow-hidden">
        <div className="max-w-6xl mx-auto">

          {/* Section Header */}
          <div className="text-center mb-16">
            <span className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-accent-soft text-accent-muted text-xs font-extrabold tracking-widest uppercase mb-4 border border-accent/20 shadow-sm">
              <span className="w-2 h-2 rounded-full bg-cta animate-ping" />
              OUR EXECUTION PIPELINE
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-primary tracking-tight mb-4">
              4 Steps to a <span className="text-gradient">pest-free space.</span>
            </h2>
            <p className="text-txt-muted text-base max-w-2xl mx-auto leading-relaxed">
              A transparent, safety-focused treatment pathway engineered for guaranteed long-term relief.
            </p>
          </div>

          {/* 4-Step Connected Horizontal Timeline (Card-Free Design) */}
          <div className="relative pt-4">
            {/* Horizontal Connecting Line (Desktop) */}
            <div className="hidden lg:block absolute top-[44px] left-[10%] right-[10%] h-1 bg-gradient-to-r from-accent/20 via-accent-bright to-accent/20 rounded-full z-0" />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-6 relative z-10">
              {[
                ['01', 'Free On-Site Inspection', 'Thorough premises scan to detect nest points, entry routes & species infestation.', 'search'],
                ['02', 'Tailored Action Plan', 'Custom treatment proposal with transparent pricing and zero hidden charges.', 'chart'],
                ['03', 'Precision Treatment', 'Applied by FoSTaC certified specialists using 100% odorless, eco-safe products.', 'zap'],
                ['04', 'Warranty & Audits', 'Scheduled post-treatment follow-ups and written multi-year warranty coverage.', 'document'],
              ].map(([step, title, desc, iconName], index) => (
                <div key={step} className="flex flex-col items-center text-center group cursor-default relative">
                  {/* Step Icon Node */}
                  <div className="w-16 h-16 rounded-2xl bg-surface border-2 border-accent shadow-lg flex items-center justify-center mb-5 relative group-hover:scale-110 group-hover:border-cta group-hover:shadow-xl transition-all duration-300">
                    <SVGIcon name={iconName} className="w-7 h-7 text-cta group-hover:scale-110 transition-transform" />
                    <span className="absolute -top-2 -right-2 bg-primary text-white text-[10px] font-extrabold w-6 h-6 rounded-full flex items-center justify-center border-2 border-surface shadow-sm">
                      {step}
                    </span>
                  </div>

                  <h3 className="font-heading text-lg font-bold text-primary mb-2 group-hover:text-cta transition-colors">
                    {title}
                  </h3>
                  <p className="text-xs text-txt-muted leading-relaxed max-w-xs">
                    {desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── 7. STATS COUNTER (Ultra-Modern 3D Glass Pods) ─── */}
      <section className="py-20 px-6 md:px-16 bg-surface-alt border-y border-border/80 relative overflow-hidden">
        {/* Ambient Glowing Orbs */}
        <div className="absolute left-1/4 top-1/2 -translate-y-1/2 w-80 h-80 rounded-full bg-accent-bright/10 blur-3xl pointer-events-none" />
        <div className="absolute right-1/4 top-1/2 -translate-y-1/2 w-80 h-80 rounded-full bg-cta/10 blur-3xl pointer-events-none" />

        <div className="max-w-6xl mx-auto relative z-10">

          {/* Section Header */}
          <div className="text-center mb-14">
            <span className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-accent-soft text-accent-muted text-xs font-extrabold tracking-widest uppercase mb-3 border border-accent/20 shadow-sm">
              <span className="w-2 h-2 rounded-full bg-cta animate-ping" />
              VERIFIED IMPACT &amp; REACH
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary tracking-tight">
              Numbers that define our <span className="text-gradient">excellence.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {[
              ['500+', 'Happy Clients', 'Residential, Commercial & Industrial', 'users'],
              ['8 Branches', 'Across Maharashtra', 'Fast Regional Dispatch', 'mapPin'],
              ['11 Services', 'Targeted Programs', 'Pre & Post Construction Shield', 'layers'],
              ['26+ Years', 'Industry Proven', 'ISO 9001 & FoSTaC Certified', 'award'],
            ].map(([num, title, sub, iconName]) => (
              <div key={title} className="card-3d-gem p-7 text-center relative group cursor-default flex flex-col items-center justify-between z-10">
                <div className="w-14 h-14 rounded-2xl bg-white shadow-sm border border-slate-100 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <SVGIcon name={iconName} className="w-8 h-8 text-cta" />
                </div>

                <div className="my-2 relative w-full z-20">
                  <span className="font-heading text-3xl sm:text-4xl lg:text-4xl font-black text-gradient block mb-1.5 tracking-tight leading-none">
                    {num}
                  </span>
                  <b className="font-heading text-base font-bold text-primary block mb-1 group-hover:text-cta transition-colors">
                    {title}
                  </b>
                  <span className="text-xs text-txt-muted block leading-normal w-full px-1">
                    {sub}
                  </span>
                </div>

                <div className="w-full pt-3.5 mt-3 border-t border-border/60 flex items-center justify-center gap-1.5 text-[11px] font-extrabold text-accent-muted">
                  <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" /> Verified Metric
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Curved Divider Top for CTA (Stats -> CTA) */}
      <div className="w-full overflow-hidden leading-none bg-surface-alt -mt-1 -mb-1 relative z-10 pointer-events-none">
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-14 text-accent-bright fill-current">
          <path d="M0,0 C150,90 350,-40 500,50 C650,140 900,10 1200,60 L1200,120 L0,120 Z" />
        </svg>
      </div>

      {/* ─── 8. TAKE THE FIRST STEP (3D Glass Matrix CTA Banner) ─── */}
      <section className="bg-gradient-to-br from-primary-dark via-primary to-primary-deep py-14 sm:py-20 px-4 sm:px-6 md:px-16 text-white relative overflow-hidden">
        {/* Ambient Glowing Background Bulbs */}
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-accent-bright/15 blur-3xl pointer-events-none float-slow" />
        <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full bg-cta/20 blur-3xl pointer-events-none float-medium" />

        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-10 items-center relative z-10">

          {/* Left Column: CTA Headings & Flashing Action Buttons */}
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 px-3.5 sm:px-4 py-1.5 sm:py-2 rounded-full bg-accent/20 text-accent-bright text-[10px] sm:text-xs font-bold tracking-widest uppercase mb-4 sm:mb-5 border border-accent/30 shadow-sm backdrop-blur-md">
              <span className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-accent animate-ping shrink-0" />
              TAKE THE FIRST STEP
            </span>

            <h2 className="font-heading text-2xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight mb-3 sm:mb-4 leading-tight">
              Protect your space <span className="text-accent-bright">today.</span>
            </h2>

            <p className="text-white/80 text-xs sm:text-base md:text-lg max-w-xl mb-6 sm:mb-8 leading-relaxed font-normal">
              Schedule a free expert on-site inspection. Fast regional response guaranteed across all Maharashtra branch locations.
            </p>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 sm:gap-3 bg-cta hover:bg-cta-hover text-white font-extrabold text-xs sm:text-base px-6 sm:px-8 py-3.5 sm:py-4 rounded-2xl glow-cta no-underline text-center transition-all hover:-translate-y-0.5">
                Book Free Inspection <span className="text-lg sm:text-xl">→</span>
              </Link>
              <a href="https://wa.me/918788895445" target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2.5 glass-dark text-white font-bold text-xs sm:text-base px-6 sm:px-8 py-3.5 sm:py-4 rounded-2xl hover:bg-white/20 transition-all no-underline shadow-md text-center">
                💬 Instant WhatsApp Chat
              </a>
            </div>
          </div>

          {/* Right Column: 3D Inspection Perks Stack Card */}
          <div className="lg:col-span-5 relative flex justify-center">
            <div className="glass-dark p-5 sm:p-7 md:p-8 rounded-3xl w-full max-w-md border border-white/20 shadow-2xl space-y-3.5 sm:space-y-4 relative">
              <div className="flex items-center justify-between border-b border-white/10 pb-3.5">
                <b className="text-xs sm:text-sm font-bold text-white uppercase tracking-wider">Inspection Guarantee</b>
                <span className="text-[10px] sm:text-xs font-bold text-accent-bright bg-white/10 px-2.5 sm:px-3 py-1 rounded-full border border-white/20">
                  FREE SERVICE
                </span>
              </div>

              {[
                ['zap', 'Same-Day On-Site Inspection', 'Fast technician dispatch across 8 districts.'],
                ['leaf', '100% Odorless & Eco Safe', 'Safe for children, pets & food prep zones.'],
                ['shield', 'Written Warranty Included', 'Backed by scheduled post-treatment audits.'],
              ].map(([iconName, title, sub]) => (
                <div key={title} className="flex items-start gap-3 sm:gap-4 p-3 sm:p-3.5 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-accent-soft/20 text-accent-bright flex items-center justify-center shrink-0 mt-0.5">
                    <SVGIcon name={iconName} className="w-4 h-4 sm:w-5 sm:h-5 text-accent-bright" />
                  </div>
                  <div>
                    <strong className="block text-xs sm:text-sm font-bold text-white mb-0.5">{title}</strong>
                    <span className="text-[10px] sm:text-xs text-white/60 block leading-tight">{sub}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* Curved Divider Bottom for Testimonials (CTA -> Testimonials) */}
      <div className="w-full overflow-hidden leading-none bg-primary-dark -mt-1 -mb-1 relative z-10 pointer-events-none">
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-14 text-accent-bright fill-current">
          <path d="M0,0 C150,90 350,-40 500,50 C650,140 900,10 1200,60 L1200,120 L0,120 Z" />
        </svg>
      </div>

      {/* ─── 9. CLIENT TESTIMONIALS & AUDITS (Redesigned Trusted by Industry Leaders) ─── */}
      <section className="py-14 sm:py-20 lg:py-24 px-4 sm:px-6 md:px-16 bg-surface-alt relative overflow-hidden">
        {/* Background Ambient Bulbs */}
        <div className="absolute right-0 top-1/4 w-96 h-96 bg-accent-bright/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute left-0 bottom-1/4 w-80 h-80 bg-cta/10 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-6xl mx-auto relative z-10">

          {/* Section Header */}
          <div className="text-center mb-10 sm:mb-16">
            <div className="inline-flex items-center gap-2 px-3.5 sm:px-4 py-1.5 sm:py-2 rounded-full bg-white text-cta text-[10px] sm:text-xs font-bold tracking-widest uppercase mb-3 sm:mb-4 border border-accent/30 shadow-2xs">
              <span className="text-amber-500 font-black">★ 4.9 / 5.0</span>
              <span className="w-1.5 h-1.5 rounded-full bg-cta" />
              <span className="truncate">500+ VERIFIED AUDITS</span>
            </div>
            <h2 className="font-heading text-2xl sm:text-4xl md:text-5xl font-bold text-primary tracking-tight mb-3 sm:mb-4">
              Trusted by <span className="text-cta">industry leaders.</span>
            </h2>
            <p className="text-txt-muted text-xs sm:text-base leading-relaxed max-w-2xl mx-auto font-medium">
              From FSSAI-audited food manufacturing plants to luxury residences, see why commercial brands &amp; homeowners rely on our ISO 9001:2015 certified eradication programmes.
            </p>
          </div>

          {/* 3D Hologram Testimonial Cards Grid with Automatic Left-to-Right Flashing Light Beam Sweep */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 items-stretch mb-10 sm:mb-16">
            {[
              {
                quote: "Outstanding service! Our manufacturing facility passed the strict FSSAI & HACCP food audit with zero pest observations after their treatment.",
                name: "Rajesh Patil",
                role: "Director of Quality Assurance",
                location: "Food Processing Facility, Pune",
                badge: "FSSAI AUDIT COMPLIANT",
                badgeColor: "bg-emerald-100 text-emerald-800 border-emerald-300",
                initials: "RP",
                avatarBg: "bg-gradient-to-br from-emerald-600 to-primary-dark text-accent-bright border-emerald-400/40",
              },
              {
                quote: "Termite infestation was completely cleared. They inspected every wall with ultrasonic scanners and gave us a written multi-year warranty certificate.",
                name: "Sneha Kulkarni",
                role: "Luxury Homeowner",
                location: "Sangli, Maharashtra",
                badge: "10-YR WARRANTY CERTIFIED",
                badgeColor: "bg-blue-100 text-blue-800 border-blue-300",
                initials: "SK",
                avatarBg: "bg-gradient-to-br from-amber-500 to-orange-600 text-white border-amber-300/50",
              },
              {
                quote: "Punctual, highly professional, and completely odorless gel treatment. Perfect for operating hours and maintaining strict commercial kitchen hygiene.",
                name: "Amit Deshmukh",
                role: "Restaurant Operations Lead",
                location: "Solapur, Maharashtra",
                badge: "100% ODORLESS KITCHEN SAFE",
                badgeColor: "bg-amber-100 text-amber-800 border-amber-300",
                initials: "AD",
                avatarBg: "bg-gradient-to-br from-cta via-emerald-600 to-teal-700 text-white border-cta/40",
              },
            ].map((review, idx) => (
              <article
                key={review.name}
                className="card-3d-hologram card-auto-sheen p-6 sm:p-8 flex flex-col justify-between bg-white rounded-3xl border border-slate-200/90 shadow-xl relative overflow-hidden"
                style={{ animationDelay: `${idx * 0.8}s` }}
              >
                {/* Auto Ambient Corner Light Pulse (Continuous, No Hover Required) */}
                <div className="absolute -top-10 -right-10 w-36 h-36 rounded-full bg-accent-bright/15 blur-2xl animate-pulse pointer-events-none" />

                <div className="relative z-10">
                  {/* Top Audit Badge & Star Rating */}
                  <div className="flex items-center justify-between gap-2 mb-4 sm:mb-5 pb-3.5 border-b border-slate-100">
                    <span className={`text-[9px] font-bold px-2.5 py-0.5 sm:py-1 rounded-full border ${review.badgeColor} tracking-wider uppercase shadow-2xs inline-flex items-center gap-1`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-cta animate-ping shrink-0" />
                      ✓ {review.badge}
                    </span>
                    <div className="text-xs font-bold text-amber-500 tracking-wider flex items-center gap-0.5">
                      ★ ★ ★ ★ ★
                    </div>
                  </div>

                  {/* Quote Body */}
                  <p className="font-body text-xs sm:text-sm text-primary leading-relaxed mb-5 sm:mb-6 font-medium italic">
                    "{review.quote}"
                  </p>
                </div>

                {/* Reviewer Profile Header */}
                <div className="pt-4 border-t border-slate-100 flex items-center gap-3 sm:gap-4 relative z-10">
                  <div className={`w-10 h-10 sm:w-11 sm:h-11 rounded-2xl ${review.avatarBg} font-heading font-extrabold text-xs sm:text-sm flex items-center justify-center shrink-0 shadow-md border`}>
                    {review.initials}
                  </div>
                  <div>
                    <strong className="block text-xs sm:text-sm font-extrabold text-primary leading-snug">{review.name}</strong>
                    <span className="text-[10px] sm:text-xs text-txt-muted block font-medium">{review.role}</span>
                    <span className="text-[9px] text-cta font-bold block mt-0.5">{review.location}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* Industry Sectors Served Trust Bar */}
          <div className="p-5 sm:p-8 rounded-3xl bg-white border border-slate-200 shadow-md">
            <span className="block text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-txt-muted text-center mb-3 sm:mb-4">
              COMMERCIAL SECTORS PROTECTED ACROSS MAHARASHTRA
            </span>
            <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
              {[
                '🏭 Food & Beverage Processing',
                '🏨 Hotels & Hospitality',
                '🏥 Healthcare & Pharmaceuticals',
                '🏢 IT Parks & Corporate Offices',
                '🛍️ Retail Malls & Warehouses',
                '🏡 Gated Residential Societies'
              ].map((sector) => (
                <span key={sector} className="text-[11px] sm:text-xs font-bold text-primary bg-surface-alt px-3.5 py-1.5 rounded-xl border border-accent/20 hover:border-accent transition-colors shadow-2xs">
                  {sector}
                </span>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* Curved Divider Top for FAQ */}
      <div className="w-full overflow-hidden leading-none bg-bg -mt-1 -mb-1 relative z-10 pointer-events-none">
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-16 text-accent-bright fill-current">
          <path d="M0,0 C150,90 350,-40 500,50 C650,140 900,10 1200,60 L1200,120 L0,120 Z" />
        </svg>
      </div>

      {/* ─── 10. FAQ ACCORDION (Interactive 3D Glass Cards) ─── */}
      <section className="py-14 sm:py-20 px-4 sm:px-6 md:px-16 bg-surface relative">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <span className="inline-flex items-center gap-2 px-3.5 sm:px-4 py-1.5 rounded-full bg-accent-soft text-accent-muted text-[10px] sm:text-xs font-bold tracking-widest uppercase mb-3 sm:mb-4 border border-accent/20 shadow-2xs">
              <span className="w-2 h-2 rounded-full bg-cta animate-ping shrink-0" />
              FREQUENTLY ASKED QUESTIONS
            </span>
            <h2 className="font-heading text-2xl sm:text-4xl md:text-5xl font-bold text-primary tracking-tight">
              Got questions? <span className="text-cta">We have answers.</span>
            </h2>
          </div>

          <div className="space-y-3.5 sm:space-y-4">
            {[
              ['Is your pest treatment safe for children & pets?', 'Yes! We exclusively use government-approved, odorless, eco-friendly formulations that are completely safe for households, pets, and children when applied by our FoSTaC certified technicians.'],
              ['How long does a standard treatment take?', 'Most residential treatments take 1 to 2 hours. Industrial complexes may vary. We provide exact timing after our initial free inspection.'],
              ['Do you provide Annual Maintenance Contracts (AMC)?', 'Yes, we offer comprehensive 1-Year and 3-Year AMC plans for residential societies, commercial offices, and industrial plants with guaranteed follow-up visits.'],
              ['Which Maharashtra cities do you cover?', 'We have 8 branch offices operating in Pune (Head Office), Sangli, Latur, Solapur, Parbhani, Mumbai, Ahilya Nagar, and Satara.'],
              ['Are your treatments audit-compliant for FSSAI / ISO?', 'Yes, our documentation and procedures fully support AIB, ISO, USFDA, FDA, HACCP, HALAL, and FSSAI audits.'],
            ].map(([q, a], i) => (
              <div key={i} className="card-3d-hologram overflow-hidden transition-all rounded-2xl bg-white border border-slate-200">
                <button
                  className="w-full text-left p-4 sm:p-6 flex justify-between items-center gap-3 bg-transparent border-0 font-heading text-sm sm:text-base md:text-lg font-bold text-primary cursor-pointer"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="pr-2">{q}</span>
                  <span className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-accent-soft text-accent-muted grid place-items-center text-lg sm:text-xl shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-45 bg-cta text-white' : ''}`}>+</span>
                </button>
                {openFaq === i && (
                  <div className="px-4 sm:px-6 pb-5 pt-1 text-xs sm:text-sm text-txt-muted leading-relaxed border-t border-slate-100 font-medium">
                    {a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

    </main>
  );
}

/* ════════════════════════════════════════
   SITE CHROME — Header, Footer, Floating WA
   ════════════════════════════════════════ */
function SiteChrome({ children }) {
  const location = useLocation();
  const isAdminArea = location.pathname.startsWith('/admin') || location.pathname === '/login' || location.pathname === '/forgot-password';
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    function handleScroll() {
      if (window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    }
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  return (
    <>
      {!isAdminArea && <Header />}
      {children}
      {!isAdminArea && <Footer />}
      {!isAdminArea && (
        <div className="fixed right-4 sm:right-5 bottom-4 sm:bottom-5 z-50 flex flex-col items-end gap-4 sm:gap-6 pointer-events-none">
          {/* Scroll to Top Button */}
          <button
            onClick={scrollToTop}
            aria-label="Scroll to top"
            className={`w-9 h-9 rounded-xl bg-accent hover:bg-accent-muted text-white shadow-md border border-white/40 flex items-center justify-center transition-all duration-300 hover:-translate-y-0.5 cursor-pointer pointer-events-auto ${showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6 pointer-events-none'
              }`}
          >
            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
            </svg>
          </button>

          {/* WhatsApp Floating Chat Bubble */}
          <div className="pointer-events-auto relative z-50 group">
            {/* Chat Bubble Message Preview (Absolute position above the button) */}
            <div className="absolute bottom-full right-0 mb-3 opacity-0 translate-y-4 pointer-events-none group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto transition-all duration-300 ease-out bg-white rounded-2xl rounded-br-sm shadow-xl border border-slate-200 p-3 sm:p-3.5 w-[220px] sm:w-[250px] origin-bottom-right cursor-default">
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-7 h-7 rounded-full bg-[#25D366] flex items-center justify-center shrink-0">
                  <svg className="w-4 h-4 fill-current text-white" viewBox="0 0 24 24"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" /></svg>
                </div>
                <div>
                  <span className="text-[10px] sm:text-[11px] font-bold text-primary block leading-tight">PPT Support</span>
                  <span className="text-[8px] sm:text-[9px] text-[#25D366] font-semibold flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#25D366] animate-pulse" />
                    Online now
                  </span>
                </div>
              </div>
              <p className="text-[10px] sm:text-[11px] text-txt-muted leading-relaxed m-0 font-medium">
                Hi! 👋 Need pest control help? Chat with us now 💬
              </p>
              <span className="text-[8px] text-txt-muted/50 block text-right mt-1">just now</span>
              {/* Chat bubble tail */}
              <div className="absolute -bottom-1.5 right-6 w-3 h-3 bg-white border-b border-r border-slate-200 rotate-45" />
            </div>

            {/* WhatsApp Circle Button */}
            <a
              href="https://wa.me/918788895445"
              target="_blank"
              rel="noreferrer"
              aria-label="WhatsApp us"
              className="relative w-14 h-14 sm:w-[60px] sm:h-[60px] rounded-full bg-[#25D366] hover:bg-[#20BD5A] text-white shadow-[0_4px_20px_rgba(37,211,102,0.5)] hover:shadow-[0_6px_30px_rgba(37,211,102,0.7)] hover:-translate-y-1 transition-all duration-300 flex items-center justify-center no-underline border-2 border-white/40 block"
            >
              {/* Pulsing ring */}
              <span className="absolute inset-0 rounded-full border-2 border-[#25D366] animate-ping opacity-30 pointer-events-none" />
              <svg className="w-7 h-7 sm:w-8 sm:h-8 fill-current relative z-10" viewBox="0 0 24 24"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" /></svg>
            </a>
          </div>
        </div>
      )}
    </>
  );
}

/* ════════════════════
   SCROLL TO TOP ON ROUTE CHANGE
   ════════════════════ */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pathname]);
  return null;
}

/* ════════════════════
   APP ROUTER
   ════════════════════ */
function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <SiteChrome>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
            <Route index element={<Navigate to="users" replace />} />
            <Route path="users" element={<UsersList />} />
            <Route path="create-user" element={<CreateUser />} />
          </Route>
          <Route path="*" element={<Home />} />
        </Routes>
      </SiteChrome>
    </BrowserRouter>
  );
}

export default App;
